// buttons.ts — THE icon-button primitive (Styles × Themes program, PR-2).
//
// Owner ruling 2026-09-05: every button in the product is ICON-ONLY, primary
// actions included. The intent COLOUR is the signifier ("it's the only
// additive state button and is obvious what it does") and the NAME lives in
// the tooltip. This module is the one place that renders that decision, so
// glyph, ramp, tooltip and aria can never disagree.
//
// It lives in pi-stack/src/client because that is the SHARED client layer all
// five surfaces already consume (the tooltip primitive moved here for the same
// reason — helm-bridge/src/client/tooltip.ts and helm-console/src/client/
// tooltip.ts are re-exports, and buttons.ts follows that precedent exactly).
//
// ── the four intents ──────────────────────────────────────────────────────
//   neutral      navigation, view-state toggles, non-committal actions. The
//                default; unlimited per view.
//   additive     THE creating / confirming action. At MOST ONE per view
//                context — the invariant that makes an icon-only primary
//                legible. `ctx` is therefore REQUIRED on an additive button
//                and buttons-check groups additive call sites by it.
//   destructive  irreversible acts. ALWAYS armed by the confirm interaction
//                below (an explicit `noConfirm:` reason is the only opt-out,
//                and it is rendered into the DOM so it is visible in the page).
//   error        state-driven attention (a thing needs intervention). Never
//                decorative.
//
// ── the three states ──────────────────────────────────────────────────────
// enabled / disabled / busy. Disabled and busy are DERIVED from whichever
// intent ramp the button rides, by ONE formula in CSS (`.ibtn[data-btn-state]`
// mixes the ramp's own fill/ink/border) — never a hand-picked colour per
// intent. Adding an intent therefore cannot forget its disabled face.
//
// ── the confirm-destructive pairing ───────────────────────────────────────
// A destructive-intent button ALWAYS carries a question. This module writes
// the DECLARATION (`data-btn-confirm`, or `data-btn-noconfirm` with a named
// reason); the two tiers that ACT on it — the capture-phase arm-then-fire and
// the hosted confirm dialog — live together in confirm.ts (PR-3b), so the
// product has one answer to "how do we ask before destroying something" and
// primitives-check has one thing to count. buttons.ts stays an import-free
// leaf, which is what keeps confirm.ts -> modal-host.ts -> buttons.ts a chain
// rather than a cycle.

export type BtnIntent = 'neutral' | 'additive' | 'destructive' | 'error';
export type BtnState = 'enabled' | 'disabled' | 'busy';
export type BtnSize = 'xs' | 'sm' | 'md' | 'lg';

export const BTN_INTENTS: readonly BtnIntent[] = ['neutral', 'additive', 'destructive', 'error'];
export const BTN_STATES: readonly BtnState[] = ['enabled', 'disabled', 'busy'];

export interface IconBtnSpec {
  /** Sprite symbol id — rendered as `#i-<glyph>`. Required: a blank glyph is
   *  this framework's signature silent failure, so there is no default. The
   *  field is named `glyph` (not `icon`) precisely so check-icon-refs can
   *  collect it across EVERY surface including pi-stack, whose own inline
   *  `icon:` tables never touch the sprite. */
  glyph: string;
  /** The button's name. THE single source: rendered as `data-tip` (the
   *  tooltip primitive's plain-text lane) AND as `aria-label`, byte for byte.
   *  buttons-check refuses a divergence. */
  label: string;
  /** Default 'neutral'. */
  intent?: BtnIntent;
  /** Default 'enabled'. */
  state?: BtnState;
  /** View context. REQUIRED when intent is 'additive' — the gate groups
   *  additive call sites by this literal and refuses a second one. Pass a
   *  stable, human-readable name for the view the button lives in
   *  ('deployments-toolbar', 'cr-decision-bar', …). Splitting a context is a
   *  deliberate design act: give the two halves different names and say why. */
  ctx?: string;
  /** The question the armed state asks. A destructive button ALWAYS arms
   *  (the question defaults to `<label>?`); any other intent may opt in by
   *  setting this — which is how a consequential-but-not-destructive act like
   *  approving a gate keeps its ask without inventing a second mechanism. The
   *  dot-pivot "Approve" pill this replaced was one of the three incompatible
   *  confirm affordances the WF-A audit measured. */
  confirm?: string;
  /** Deliberate opt-out of the confirm gate for a destructive-INTENT button
   *  whose confirmation already happens elsewhere (a modal the click opens, a
   *  typed-name field). Must name why; the value is rendered into the DOM as
   *  `data-btn-noconfirm` so it is visible in the page, not only in source. */
  noConfirm?: string;
  /** Default 'md' (34px — the height `.ctrl` has always been). 'xs' (22px) is
   *  the compact pane-header well the agent and workflow panes use. */
  size?: BtnSize;
  /** `type="button"` unless a form needs this to BE the submit control.
   *  Declared here rather than through `attrs` because the primitive writes
   *  `type` itself and a duplicate attribute silently loses (the first wins). */
  type?: 'button' | 'submit' | 'reset';
  /** Extra classes. LAYOUT ONLY — colour belongs to the intent ramp. */
  cls?: string;
  id?: string;
  /** Extra attributes (data-act, data-id, aria-expanded, …). A null/undefined
   *  value drops the attribute; `true` renders it bare. */
  attrs?: Record<string, string | number | boolean | null | undefined>;
}

const ESC: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
const esc = (s: string): string => String(s).replace(/[&<>"']/g, (c) => ESC[c] as string);

/** Every attribute the primitive owns, in render order. Exported so the gate
 *  can assert the contract against the built output rather than a guess. */
export const BTN_OWNED_ATTRS = [
  'type', 'class', 'data-btn', 'data-btn-state', 'data-btn-ctx',
  'data-btn-confirm', 'data-btn-noconfirm', 'data-tip', 'aria-label',
] as const;

function specAttrs(spec: IconBtnSpec): Array<[string, string | true]> {
  const intent: BtnIntent = spec.intent ?? 'neutral';
  const state: BtnState = spec.state ?? 'enabled';
  if (!spec.glyph) throw new Error('iconBtn: glyph is required — a blank glyph is a silent failure');
  if (!spec.label) throw new Error('iconBtn: label is required — it is both the tooltip and the aria-label');
  if (intent === 'additive' && !spec.ctx) {
    throw new Error(
      `iconBtn: the additive intent requires a view context (ctx) — "${spec.label}" must declare which view it is the one additive action of`,
    );
  }
  const out: Array<[string, string | true]> = [
    ['type', spec.type ?? 'button'],
    ['class', ['ibtn', `ibtn-${spec.size ?? 'md'}`, spec.cls ?? ''].filter(Boolean).join(' ')],
    ['data-btn', intent],
    ['data-btn-state', state],
  ];
  if (spec.ctx) out.push(['data-btn-ctx', spec.ctx]);
  if (spec.noConfirm) out.push(['data-btn-noconfirm', spec.noConfirm]);
  else if (intent === 'destructive') out.push(['data-btn-confirm', spec.confirm ?? `${spec.label}?`]);
  else if (spec.confirm) out.push(['data-btn-confirm', spec.confirm]);
  out.push(['data-tip', spec.label], ['aria-label', spec.label]);
  if (spec.id) out.push(['id', spec.id]);
  if (state !== 'enabled') out.push(['disabled', true]);
  if (state === 'busy') out.push(['aria-busy', 'true']);
  for (const [k, v] of Object.entries(spec.attrs ?? {})) {
    if (v === null || v === undefined || v === false) continue;
    out.push([k, v === true ? true : String(v)]);
  }
  return out;
}

/**
 * THE DECORATION SLOT (W1-1). A style's hover/press face — Starship's target-
 * lock reticle, Destroyer's lock, Quarterdeck's compass ring, Glass's accent
 * ring, Synthwave's bloom — is drawn by `.ibtn-deco`, and it is a real child
 * rather than a pseudo-element because BOTH pseudo-elements of `.ibtn` are
 * already claimed: `::before` by Starship's focus bracket and `::after` by
 * the busy face. A style claiming either would silently erase a rule it could
 * not see, which is exactly why the shipped styles carried ZERO `.ibtn:hover`
 * rules across all seven files.
 *
 * It is emitted UNCONDITIONALLY, by both lanes, so that a style's one
 * `--btn-deco` declaration works on every surface without a markup change —
 * and buttons-check refuses a surface that declares `--btn-deco` without it.
 * Under Blueprint it paints nothing (`--btn-deco` is `none`, `opacity: 0`).
 */
export const IBTN_DECO_HTML = '<span class="ibtn-deco" aria-hidden="true"></span>';

/** The HTML-string lane — the shape most of this codebase renders in. */
export function iconBtn(spec: IconBtnSpec): string {
  const attrs = specAttrs(spec)
    .map(([k, v]) => (v === true ? ` ${k}` : ` ${k}="${esc(v)}"`))
    .join('');
  return `<button${attrs}><svg class="icon ibtn-glyph" aria-hidden="true"><use href="#i-${esc(spec.glyph)}"/></svg>${IBTN_DECO_HTML}</button>`;
}

/** The DOM lane — same contract, same attributes, one implementation. */
export function mkIconBtn(spec: IconBtnSpec): HTMLButtonElement {
  const btn = document.createElement('button');
  for (const [k, v] of specAttrs(spec)) {
    // class / id / type go through their PROPERTIES, not setAttribute. A real
    // DOM reflects either way; the console's stub-document harness reflects
    // only the property, and a button whose class arrived as an attribute was
    // invisible to every `querySelector('.x')` in those suites.
    if (k === 'disabled') btn.disabled = true;
    else if (k === 'class' && v !== true) { btn.className = v; for (const c of v.split(/\s+/)) if (c) btn.classList.add(c); }
    else if (k === 'id' && v !== true) btn.id = v;
    else if (k === 'type' && v !== true) btn.type = v as 'button' | 'submit' | 'reset';
    else btn.setAttribute?.(k, v === true ? '' : v);
  }
  btn.innerHTML = `<svg class="icon ibtn-glyph" aria-hidden="true"><use href="#i-${esc(spec.glyph)}"/></svg>${IBTN_DECO_HTML}`;
  return btn;
}

/** Move a rendered button between states. The ramp does the rest. */
export function setBtnState(btn: HTMLButtonElement | null | undefined, state: BtnState): void {
  if (!btn) return;
  btn.dataset.btnState = state;
  btn.disabled = state !== 'enabled';
  // `?.` because several console modules are contractually STUB-DRIVABLE
  // (helm-console/scripts/client-dom-stub.mjs), and a primitive that only
  // works against a real Element pushes every such call site into forking it
  // — which is the defect this module exists to remove.
  if (state === 'busy') btn.setAttribute?.('aria-busy', 'true');
  else btn.removeAttribute?.('aria-busy');
}

/** Repoint a rendered button's glyph (state changes that swap the symbol —
 *  play/pause, expand/collapse — stay one button, not two). */
export function setBtnGlyph(btn: HTMLElement | null | undefined, glyph: string): void {
  btn?.querySelector('use')?.setAttribute('href', `#i-${glyph}`);
}

/** Move a rendered button between intents (a control whose MEANING changes with
 *  state — a reset that is inert at the default and an attention-grade action
 *  once the value differs — stays one button rather than two). */
export function setBtnIntent(btn: HTMLElement | null | undefined, intent: BtnIntent): void {
  if (btn) btn.dataset.btn = intent;
}

/** Retarget both halves of the label at once, so they can never drift. */
export function setBtnLabel(btn: HTMLElement | null | undefined, label: string): void {
  if (!btn) return;
  btn.setAttribute?.('data-tip', label);
  btn.setAttribute?.('aria-label', label);
}

// ---------------------------------------------------------------- confirm
//
// THE CONFIRM-DESTRUCTIVE PRIMITIVE MOVED OUT (Styles x Themes PR-3b) to
// pi-stack/src/client/confirm.ts, where the capture-phase arm-then-fire tier
// now sits beside the HOSTED confirm dialog on the one modal host. Both tiers
// in one module is what makes "how does this product ask before it destroys
// something" answerable with a single import, and it is what primitives-check
// counts against every destructive-intent site.
//
// This module keeps the ATTRIBUTES that declare the pairing (`data-btn-
// confirm` / `data-btn-noconfirm`, written by specAttrs above) and nothing
// else: buttons.ts stays a leaf with no imports, which is what keeps
// confirm.ts -> modal-host.ts -> buttons.ts a chain instead of a cycle.
//
//   import { installConfirmDestructive } from './confirm.js';
